import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import apiService from '../services/apiService';
import { PERIOD_OPTIONS } from './PeriodSelector';
import './MPPTChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SS_TABS = [
  { id: 'voltage', label: 'Voltage', unit: 'V', field: 'avgVoltage', color: 'rgb(34, 197, 94)' },
  { id: 'current', label: 'Current', unit: 'A', field: 'avgCurrent', color: 'rgb(59, 130, 246)' },
  { id: 'power', label: 'Power', unit: 'W', field: 'avgPower', color: 'rgb(251, 191, 36)' },
  { id: 'soc', label: 'SoC', unit: '%', field: 'avgSoc', color: 'rgb(168, 85, 247)' },
  { id: 'consumed', label: 'Consumed Ah', unit: 'Ah', field: 'avgConsumedAh', color: 'rgb(239, 68, 68)' },
  { id: 'remaining', label: 'Remaining Mins', unit: 'min', field: 'avgRemainingMins', color: 'rgb(14, 165, 233)' }
];

const SmartShuntChart = ({ selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month }) => {
  const [data, setData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('voltage');

  const loadData = useCallback(async (period, year, month, year12, month12) => {
    try {
      setLoading(true);
      setError(null);

      const periodConfig = PERIOD_OPTIONS.find(p => p.id === period);
      let response;
      if (period === 'select') {
        response = await apiService.getSmartshuntData(null, year, month);
      } else if (period === '12months') {
        response = await apiService.getSmartshuntData(12, year12, month12);
      } else {
        response = await apiService.getSmartshuntData(periodConfig.months);
      }

      if (!response || !response.data || response.data.length === 0) {
        setError('No SmartShunt data available for this period');
        setData(null);
        setChartData(null);
        return;
      }

      setData(response);
    } catch (err) {
      console.error('Failed to load SmartShunt data:', err);
      setError(err.message || 'Failed to load SmartShunt data');
    } finally {
      setLoading(false);
    }
  }, []);

  const buildChartData = useCallback((response, period, tab) => {
    if (!response?.data || response.data.length === 0) {
      setChartData(null);
      return;
    }

    const dataPoints = response.data.length;
    const useMonthYear = period === 'all' || period === '12months';

    const labels = response.data.map(d => {
      if (d.day) {
        const date = new Date(d.day);
        if (useMonthYear) {
          const m = date.toLocaleDateString('en-US', { month: 'short' });
          const y = date.getFullYear().toString().slice(-2);
          return `${m} ${y}`;
        }
        const m = date.toLocaleDateString('en-US', { month: 'short' });
        return `${m} ${date.getDate()}`;
      }
      return d.label || 'N/A';
    });

    const getValue = (d, field) => {
      if (!d.hasData) return null;
      const val = parseFloat(d[field]);
      return isNaN(val) ? null : val;
    };

    const tabConfig = SS_TABS.find(t => t.id === tab);
    const pointRadius = dataPoints > 24 ? 2 : 4;

    const datasets = [
      {
        label: tabConfig.label,
        data: response.data.map(d => getValue(d, tabConfig.field)),
        borderColor: tabConfig.color,
        backgroundColor: tabConfig.color.replace('rgb', 'rgba').replace(')', ', 0.1)'),
        borderWidth: 2,
        pointRadius,
        pointHoverRadius: 6,
        tension: 0.3,
        spanGaps: false
      }
    ];

    setChartData({ labels, datasets });
  }, []);

  useEffect(() => {
    loadData(selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month);
  }, [selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month, loadData]);

  useEffect(() => {
    if (data) {
      buildChartData(data, selectedPeriod, selectedTab);
    }
  }, [data, selectedPeriod, selectedTab, buildChartData]);

  const getOptions = () => {
    const tabConfig = SS_TABS.find(t => t.id === selectedTab);
    const unit = tabConfig?.unit || '';

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: 'rgba(255, 255, 255, 0.8)',
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1,
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.y}${unit}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: 'rgba(255, 255, 255, 0.6)',
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 12
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          ticks: {
            color: 'rgba(255, 255, 255, 0.6)',
            callback: function(value) {
              return value + unit;
            }
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        }
      }
    };
  };

  return (
    <div className="mppt-chart">
      <div className="chart-header">
        <h3>SmartShunt</h3>
        <div className="mppt-tabs">
          {SS_TABS.map(tab => (
            <button
              key={tab.id}
              className={`mppt-tab ${selectedTab === tab.id ? 'active' : ''}`}
              onClick={() => setSelectedTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {data && (
        <div className="mppt-stats">
          <div className="stat-item">
            <span className="stat-label">Data Coverage</span>
            <span className="stat-value">{data.daysWithData}/{data.totalDays} days</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Readings</span>
            <span className="stat-value">{data.totalReadings?.toLocaleString()}</span>
          </div>
        </div>
      )}

      <div className="chart-container">
        {loading ? (
          <div className="chart-loading">Loading SmartShunt data...</div>
        ) : error ? (
          <div className="chart-error">{error}</div>
        ) : (
          chartData && <Line options={getOptions()} data={chartData} />
        )}
      </div>
    </div>
  );
};

export default SmartShuntChart;
